import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CalendarX2, CheckCircle2, CalendarClock } from 'lucide-react';
import { publicRdvCancellationApi } from '../../lib/api';

interface RdvInfo {
    company_name: string;
    subject: string;
    date: string;
    heure: string;
    already_cancelled: boolean;
    cancel_reason: string;
    prenom: string;
    nom: string;
}

export const RdvCancellationPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [info, setInfo] = React.useState<RdvInfo | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [reason, setReason] = React.useState('');
    const [cancelling, setCancelling] = React.useState(false);
    const [cancelled, setCancelled] = React.useState(false);

    React.useEffect(() => {
        if (!token) return;
        publicRdvCancellationApi.getInfo(token).then((d: any) => {
            setInfo(d);
            setCancelled(!!d?.already_cancelled);
        }).catch((e: any) => setError(e?.message || 'Lien invalide ou expiré.')).finally(() => setLoading(false));
    }, [token]);

    const handleCancel = async () => {
        if (!token || !reason.trim()) return;
        setCancelling(true);
        setError('');
        try {
            await publicRdvCancellationApi.cancel(token, reason.trim());
            setCancelled(true);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'annulation.");
        } finally {
            setCancelling(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-surface-app"><Loader2 size={32} className="animate-spin text-content-muted" /></div>;
    }

    if (error && !info) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4 text-center">
                <p className="text-content font-bold mb-1">Lien invalide</p>
                <p className="text-content-muted text-sm">{error}</p>
            </div>
        );
    }

    if (!info) return null;

    return (
        <div className="min-h-screen bg-surface-app py-10 px-4 flex items-center justify-center">
            <div className="max-w-md w-full bg-surface rounded-2xl border border-border p-8 space-y-6">
                <div className="text-center space-y-1">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-surface-soft border border-border flex items-center justify-center mb-3">
                        <CalendarClock size={26} className="text-content-muted" />
                    </div>
                    <h1 className="text-lg font-bold text-content">{info.company_name}</h1>
                    <p className="text-sm text-content-muted">{info.subject || 'Votre rendez-vous'}</p>
                </div>

                <div className="bg-surface-soft rounded-xl border border-border p-4 space-y-1 text-center">
                    <p className="text-sm text-content">Le <span className="font-bold">{info.date}</span> à <span className="font-bold">{info.heure}</span></p>
                </div>

                {error && <p className="text-xs text-red-500 font-semibold text-center">{error}</p>}

                {cancelled ? (
                    <div className="flex flex-col items-center gap-2 text-center py-2">
                        <CheckCircle2 size={32} className="text-green-500" />
                        <p className="text-sm font-bold text-content">Rendez-vous annulé</p>
                        <p className="text-xs text-content-muted">{info.company_name} a été prévenu de votre annulation.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5">Motif de l'annulation</label>
                            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                                placeholder="Indiquez la raison de votre annulation"
                                className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                        </div>
                        <button onClick={handleCancel} disabled={cancelling || !reason.trim()}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm disabled:opacity-50">
                            {cancelling ? <Loader2 size={16} className="animate-spin" /> : <CalendarX2 size={16} />}
                            {cancelling ? 'Annulation...' : "Confirmer l'annulation"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
